/**
 * After-action report — the game-over overlay as a typed staff report: verdict and stamp,
 * final standings (the table view of the charts), key statistics, the course of the war as a
 * line chart (victory points or believed territory per nation, collected client-side by the
 * chronicle) and a timeline of the big moments. "Inspect the map" folds the report away, and —
 * once the host has sent the war's replay log — "The Fog Lifts" replays the whole war.
 */
import { dayOf, type FactionPublic, type PlayerView, type Tick } from '@friction/engine';
import {
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from 'zustand';
import { keyMoments, niceTicks, placeEndLabels } from '../../game/aar';
import { bus } from '../../game/bus';
import { type ChronicleSample, chronicleStore } from '../../game/chronicle';
import { cssColor, formatDate, formatStamp } from '../../game/format';
import { useGame } from '../../game/store';
import { type Translator, useT } from '../../i18n';
import { nationLabel, nationName, theatreTitle } from '../../i18n/names';
import { lobbyStore } from '../../net/lobby';
import { openReplay, useReplay } from '../../replay/store';
import { Button, cx } from '../common/controls';
import { Paper, Stamp } from '../common/Panel';
import { SaveRecordButton } from '../replay/SaveRecord';
import s from './aar.module.css';
import hud from './hud.module.css';

type Metric = 'vp' | 'hexes';

const reasonOf = (t: Translator, reason: string): string =>
  reason === 'conquest' || reason === 'victory-points' || reason === 'surrender'
    ? t(`aar.reason.${reason}`)
    : '';

const H = 206;
const M = { left: 34, right: 104, top: 12, bottom: 22 };

function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const metricOf = (smp: ChronicleSample, metric: Metric, id: number) =>
  (metric === 'vp' ? smp.vp[id] : smp.hexes[id]) ?? 0;

/** The course of the war: one line per nation, direct end labels, crosshair readout. */
function WarChart({
  samples,
  factions,
  me,
  metric,
  threshold,
}: {
  samples: ChronicleSample[];
  factions: FactionPublic[];
  me: number;
  metric: Metric;
  /** Horizontal reference line (VP needed for a decisive victory), or null. */
  threshold: { value: number; label: string } | null;
}) {
  const t = useT();
  const [box, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const label = (f: FactionPublic) => nationLabel(f, t.locale);
  const W = Math.max(320, width);
  const t0 = samples[0]?.tick ?? 0;
  const t1 = Math.max(t0 + 1, samples[samples.length - 1]?.tick ?? 1);
  const peak = Math.max(
    threshold?.value ?? 0,
    ...samples.flatMap((smp) => factions.map((f) => metricOf(smp, metric, f.id))),
    1,
  );
  const yTicks = niceTicks(peak);
  const yMax = yTicks[yTicks.length - 1];
  const x = (t: Tick) => M.left + ((t - t0) / (t1 - t0)) * (W - M.left - M.right);
  const y = (v: number) => M.top + (1 - v / yMax) * (H - M.top - M.bottom);

  // Day ticks at 06:00 (every tick that is a multiple of 24), thinned to at most ~7 labels.
  const days = Math.max(1, Math.ceil((t1 - t0) / 24));
  const every = Math.max(1, Math.ceil(days / 6));
  const xTicks: Tick[] = [];
  for (let t = Math.ceil(t0 / 24) * 24; t <= t1; t += 24 * every) xTicks.push(t);

  const last = samples[samples.length - 1];
  const order = [...factions].sort((a, b) => (a.id === me ? 1 : 0) - (b.id === me ? 1 : 0));
  const ends = factions.map((f) => (last ? y(metricOf(last, metric, f.id)) : 0));
  const labelYs = placeEndLabels(ends, 13, M.top + 4, H - M.bottom);

  const pick = (clientX: number, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const px = ((clientX - r.left) / r.width) * W;
    const t = t0 + ((px - M.left) / (W - M.left - M.right)) * (t1 - t0);
    let best = 0;
    for (let i = 1; i < samples.length; i++)
      if (Math.abs(samples[i].tick - t) < Math.abs(samples[best].tick - t)) best = i;
    setHover(best);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const d = e.key === 'ArrowLeft' ? -1 : 1;
    setHover((h) => Math.max(0, Math.min(samples.length - 1, (h ?? samples.length - 1) + d)));
  };
  const hs = hover !== null ? samples[hover] : null;
  const readout = (smp: ChronicleSample | undefined) =>
    smp
      ? `${formatDate(smp.tick, t.locale)}: ${factions.map((f) => `${label(f)} ${metricOf(smp, metric, f.id)}`).join(', ')}`
      : '';
  const hx = hs ? x(hs.tick) : 0;

  return (
    <div ref={box} className={s.chartBox}>
      {width > 0 && samples.length > 0 && (
        // A time scrubber: arrow keys walk the readout through the war, like the pointer does.
        <div
          className={s.scrub}
          tabIndex={0}
          role="slider"
          aria-label={t('aar.chart.slider', {
            metric: t(metric === 'vp' ? 'aar.chart.vp' : 'aar.chart.hexes'),
          })}
          aria-valuemin={0}
          aria-valuemax={samples.length - 1}
          aria-valuenow={hover ?? samples.length - 1}
          aria-valuetext={readout(samples[hover ?? samples.length - 1])}
          onPointerMove={(e: PointerEvent<HTMLDivElement>) => pick(e.clientX, e.currentTarget)}
          onPointerLeave={() => setHover(null)}
          onFocus={() => setHover(samples.length - 1)}
          onBlur={() => setHover(null)}
          onKeyDown={onKey}
        >
          <svg width={W} height={H} className={s.chart} aria-hidden>
            {yTicks.map((v) => (
              <g key={v}>
                <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} className={s.grid} />
                <text x={M.left - 6} y={y(v) + 3} className={s.axis} textAnchor="end">
                  {v}
                </text>
              </g>
            ))}
            {xTicks.map((tk) => (
              <text key={tk} x={x(tk)} y={H - 6} className={s.axis} textAnchor="middle">
                {t('time.dayShort', { n: dayOf(tk) })}
              </text>
            ))}
            {threshold && (
              <g>
                <line
                  x1={M.left}
                  x2={W - M.right}
                  y1={y(threshold.value)}
                  y2={y(threshold.value)}
                  className={s.threshold}
                />
                <text x={M.left + 4} y={y(threshold.value) - 4} className={s.thresholdLabel}>
                  {threshold.label}
                </text>
              </g>
            )}
            {order.map((f) => (
              <polyline
                key={f.id}
                points={samples
                  .map((smp) => `${x(smp.tick).toFixed(1)},${y(metricOf(smp, metric, f.id)).toFixed(1)}`)
                  .join(' ')}
                className={s.line}
                style={{
                  stroke: cssColor(f.color),
                  strokeWidth: f.id === me ? 2.75 : 2,
                  opacity: f.alive ? 1 : 0.6,
                }}
              />
            ))}
            {last &&
              factions.map((f, i) => {
                const ex = x(last.tick);
                const moved = Math.abs(labelYs[i] - ends[i]) > 2;
                return (
                  <g key={f.id}>
                    {moved && (
                      <polyline
                        points={`${ex + 5},${ends[i]} ${ex + 10},${labelYs[i]} ${ex + 13},${labelYs[i]}`}
                        className={s.leader}
                      />
                    )}
                    <circle
                      cx={ex}
                      cy={ends[i]}
                      r={4}
                      className={s.dot}
                      style={{ fill: cssColor(f.color) }}
                    />
                    <text x={ex + 15} y={labelYs[i] + 3.5} className={cx(s.endLabel, f.id === me && s.endMe)}>
                      {label(f)} {metricOf(last, metric, f.id)}
                    </text>
                  </g>
                );
              })}
            {hs && (
              <g pointerEvents="none">
                <line x1={hx} x2={hx} y1={M.top} y2={H - M.bottom} className={s.crosshair} />
                {factions.map((f) => (
                  <circle
                    key={f.id}
                    cx={hx}
                    cy={y(metricOf(hs, metric, f.id))}
                    r={4}
                    className={s.dot}
                    style={{ fill: cssColor(f.color) }}
                  />
                ))}
              </g>
            )}
          </svg>
        </div>
      )}
      {hs && (
        <div
          className={s.tip}
          style={{ left: Math.min(hx + 12, W - 150), top: M.top }}
          role="status"
          aria-live="polite"
        >
          <div className={s.tipHead}>{formatDate(hs.tick, t.locale)}</div>
          {[...factions]
            .sort((a, b) => metricOf(hs, metric, b.id) - metricOf(hs, metric, a.id))
            .map((f) => (
              <div key={f.id} className={s.tipRow}>
                <i style={{ background: cssColor(f.color) }} />
                <b>{metricOf(hs, metric, f.id)}</b>
                <span>{label(f)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Standings({ view, me, samples }: { view: PlayerView; me: number; samples: ChronicleSample[] }) {
  const t = useT();
  const last = samples[samples.length - 1];
  const rows = [...view.factions].sort((a, b) => b.vp - a.vp);
  return (
    <table className={s.table}>
      <thead>
        <tr>
          <th>{t('aar.col.nation')}</th>
          <th>{t('aar.col.vp')}</th>
          <th>{t('aar.col.peak')}</th>
          <th>{t('aar.col.hexes')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => (
          <tr key={f.id} className={cx(f.id === me && s.rowMe)}>
            <td>
              <span className={s.swatch} style={{ background: cssColor(f.color) }} />
              {nationName(f, t.locale)}
              {view.winners?.includes(f.id) ? (
                <em>{t('aar.victor')}</em>
              ) : !f.alive ? (
                <em>{t('aar.capitulated')}</em>
              ) : null}
            </td>
            <td>{f.vp}</td>
            <td>{samples.reduce((m, smp) => Math.max(m, smp.vp[f.id] ?? 0), f.vp)}</td>
            <td>{last?.hexes[f.id] ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AfterActionReport() {
  const t = useT();
  const view = useGame((g) => g.view);
  // An observer's view is someone else's picture; the verdict and record are our own staff's.
  const ownView = useGame((g) => g.ownView);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const share = useGame((g) => g.config?.vpVictoryShare ?? 0.6);
  const samples = useStore(chronicleStore, (c) => c.samples);
  const events = useStore(chronicleStore, (c) => c.events);
  const [inspect, setInspect] = useState(false);
  const [metric, setMetric] = useState<Metric>('vp');
  const canReplay = useReplay((r) => r.log !== null);
  const played = useRef(false);
  useEffect(() => {
    if (played.current) return;
    played.current = true;
    const t = window.setTimeout(() => bus.emit('sound', { name: 'stamp' }), 420);
    return () => clearTimeout(t);
  }, []);
  const moments = useMemo(
    () => (view && map && grid ? keyMoments(events, { view, map, grid }, 10, t.locale) : []),
    [events, view, map, grid, t.locale],
  );
  if (!view?.winners || !map) return null;
  const me = ownView?.me ?? view.me;
  const winners = view.factions.filter((f) => view.winners!.includes(f.id));
  const won = winners.some((f) => f.id === me.id || f.team === me.team);
  const over = [...events].reverse().find((e) => e.kind === 'gameOver');
  const reason = over?.kind === 'gameOver' ? reasonOf(t, over.reason) : '';
  const st = me.stats;
  const totalVp = map.cities.reduce((a, c) => a + c.vp, 0);
  const quit = () => lobbyStore.getState().quitGame();
  const show = (hex: number) => {
    if (hex < 0) return;
    setInspect(true);
    bus.emit('focusHex', { hex });
  };

  if (inspect)
    return (
      <div className={`${hud.plate} ${s.returnPill}`}>
        <Button variant="secondary" icon="intel" onClick={() => setInspect(false)}>
          {t('aar.report')}
        </Button>
        {canReplay && (
          <Button variant="secondary" icon="play" onClick={() => openReplay()}>
            {t('aar.fog.title')}
          </Button>
        )}
        {canReplay && <SaveRecordButton />}
        <Button variant="primary" icon="exit" onClick={quit}>
          {t('aar.menu')}
        </Button>
      </div>
    );

  return (
    <div className={s.backdrop}>
      <Paper className={s.report}>
        <div className={s.head}>
          <span>{t('aar.head')}</span>
          <span>{theatreTitle(map.name, t.locale).toUpperCase()}</span>
          <span>{formatDate(view.tick, t.locale).toUpperCase()}</span>
        </div>
        <div className={s.cols}>
          <section className={s.left}>
            <div className={s.verdict} style={{ color: won ? '#2f4a2c' : 'var(--ink-red)' }}>
              {won ? t('aar.victory') : t('aar.defeat')}
            </div>
            <div className={s.verdictSub}>
              {t(won ? 'aar.won' : 'aar.lost', { reason, days: dayOf(view.tick) })}
            </div>
            <div className={s.sectionTitle}>{t('aar.standings')}</div>
            <Standings view={view} me={me.id} samples={samples} />
            <div className={s.sectionTitle}>{t('aar.record')}</div>
            <div className={s.stats}>
              {(
                [
                  [t('aar.stat.raised'), st.unitsProduced],
                  [t('aar.stat.lost'), st.unitsLost],
                  [t('aar.stat.destroyed'), st.enemyUnitsDestroyed],
                  [t('aar.stat.cities'), st.citiesCaptured],
                  [t('aar.stat.read'), st.ordersIntercepted],
                  [t('aar.stat.couriers'), st.couriersLost],
                  [t('aar.stat.peak'), st.peakHexes],
                  [t('aar.stat.vp'), me.vp],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className={s.stat}>
                  <span>{k}</span>
                  <b>{v}</b>
                </div>
              ))}
            </div>
            <div className={s.stampSlot}>
              <Stamp className={s.bigStamp} tone={won ? 'green' : 'red'} rotate={-12} animate delay={380}>
                {won
                  ? t('aar.stamp.concluded')
                  : me.alive
                    ? t('aar.stamp.armistice')
                    : t('aar.stamp.capitulation')}
              </Stamp>
            </div>
            {canReplay && (
              <button type="button" className={s.fogLifts} onClick={() => openReplay()}>
                <span className={s.fogKicker}>{t('aar.fog.kicker')}</span>
                <span className={s.fogTitle}>{t('aar.fog.title')}</span>
                <span className={s.fogSub}>{t('aar.fog.sub')}</span>
              </button>
            )}
            {canReplay && (
              <div className={s.saveRow}>
                <SaveRecordButton variant="ink" />
              </div>
            )}
            <div className={s.btns}>
              <Button icon="map" onClick={() => setInspect(true)}>
                {t('aar.inspect')}
              </Button>
              <Button variant="primary" icon="exit" onClick={quit}>
                {t('aar.menu')}
              </Button>
            </div>
          </section>

          <section className={s.right}>
            <div className={s.chartHead}>
              <div className={s.tabs} role="tablist" aria-label={t('aar.chart.aria')}>
                {(
                  [
                    ['vp', t('aar.chart.vp')],
                    ['hexes', t('aar.chart.hexes')],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={metric === k}
                    className={cx(s.tab, metric === k && s.tabOn)}
                    onClick={() => setMetric(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className={s.legend}>
                {view.factions.map((f) => (
                  <span key={f.id}>
                    <i style={{ background: cssColor(f.color) }} />
                    {nationLabel(f, t.locale)}
                    {f.id === me.id ? t('aar.chart.us') : ''}
                  </span>
                ))}
              </div>
            </div>
            <div className={s.chartNote}>
              {metric === 'vp' ? t('aar.chart.vpNote') : t('aar.chart.hexesNote')}
            </div>
            <WarChart
              samples={samples}
              factions={view.factions}
              me={me.id}
              metric={metric}
              threshold={
                metric === 'vp'
                  ? {
                      value: Math.ceil(share * totalVp),
                      label: t('aar.chart.decisive', { pct: t.pct(share) }),
                    }
                  : null
              }
            />
            <div className={s.sectionTitle}>{t('aar.course')}</div>
            <ol className={s.timeline}>
              {moments.map((m) => (
                <li key={`${m.tick}-${m.head}-${m.text}`}>
                  <button
                    type="button"
                    className={cx(s.moment, m.severity === 'urgent' && s.urgent, m.hex >= 0 && s.clickable)}
                    onClick={() => show(m.hex)}
                    disabled={m.hex < 0}
                    title={m.hex >= 0 ? t('ui.showOnTheMap') : undefined}
                  >
                    <span className={s.when}>{formatStamp(m.tick, t.locale)}</span>
                    <span>
                      {m.head && <span className={s.headWord}>{m.head} — </span>}
                      {m.text}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </Paper>
    </div>
  );
}
